import React from 'react'
import { css } from '@emotion/core'
import { Cell, TextField, InputGroup, Grid, SubheaderBlock, useAtSize, useZionFormContext } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { boolAttr } from '@fs/zion-frontend-friends'
import FadeIn from './FadeIn'
import PlaceField from './PlaceField'

const rightColumnCss = css`
  position: relative;

  --background-primary: white;
  --text-primary: var(--border-primary);

  &[data-layout='condensed'] {
    margin-top: -36px;
  }

  &[data-layout='condensed'][is-mobile] {
    margin-top: -12px;
  }

  & .input-base {
    margin-bottom: 0;
  }
`

const removeIconButtonCss = css`
  position: absolute;
  right: -48px;
  top: 28px;

  &[data-orientation='vertical'] {
    top: -44px;
  }
`

const mobileRemoveIconButtonCss = css`
  position: absolute;
  right: -8px;
  top: -116px;
`

const verticalRemoveIconButtonCss = css`
  position: absolute;
  right: -8px;
  top: -116px;
`

export default function EventFields({
  name,
  type,
  orientation,
}: {
  name?: string
  type?: string
  orientation?: string
}): JSX.Element {
  const [t] = useTranslation()
  const atSize = useAtSize()
  const { makeFormComponent, values, setFieldValue } = useZionFormContext()
  const isMobile = atSize({ default: true, md: false })
  const fullWidth = isMobile || orientation === 'vertical'
  const layout = fullWidth ? 'condensed' : ''
  const dateValues = values[name]?.date
  const yearLabel = fullWidth
    ? t('ancestors.year.range.condensed-label', 'Year (Range)')
    : t(`ancestors.${type}.range.label`, 'Year (Range)')

  // Following functions auto fill date inputs left blank in order to encourage more narrow search parameters.
  function handleFromBlur(): void {
    if (dateValues?.begin && !dateValues?.end) {
      setFieldValue(`${name}.date.end`, dateValues.begin)
    }
  }

  function handleToBlur(): void {
    if (dateValues?.end && !dateValues?.begin) {
      setFieldValue(`${name}.date.begin`, dateValues.end)
    }
  }

  return makeFormComponent(
    <FadeIn>
      <Grid>
        <Cell columns={fullWidth ? 12 : 6}>
          {fullWidth && <SubheaderBlock size="sm" heading={t(`ancestors.${type}.header`, 'Event')} />}
          <PlaceField name={name} type={type} orientation={orientation} />
        </Cell>
        <Cell columns={fullWidth ? 12 : 6}>
          <div css={rightColumnCss} data-layout={layout} data-orientation={orientation} is-mobile={boolAttr(isMobile)}>
            <InputGroup label={yearLabel}>
              <Grid columns={orientation === 'vertical' ? 12 : 6}>
                <Cell columns={orientation === 'vertical' ? 6 : 3}>
                  <TextField
                    placeholder={t(`ancestors.from.label`, 'From')}
                    name={`${name}.date.begin`}
                    data-testid={`${name}-range-from`}
                    onBlur={handleFromBlur}
                  />
                </Cell>
                <Cell columns={orientation === 'vertical' ? 6 : 3}>
                  <TextField
                    placeholder={t(`ancestors.to.label`, 'To')}
                    name={`${name}.date.end`}
                    data-testid={`${name}-range-to`}
                    onBlur={handleToBlur}
                  />
                </Cell>
              </Grid>
            </InputGroup>
          </div>
        </Cell>
      </Grid>
    </FadeIn>
  )
}
